import {
  Flex,
  Text,
  Badge,
  Dialog,
  Button,
  IconButton,
} from "@radix-ui/themes";
import { useState, useCallback } from "react";
import {
  ListView,
  ItemRendererProps,
} from "@/v2/components/list-view/ListView";
import { Notice } from "@/v2/entities/notice/Notice.model";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { DotsVerticalIcon } from "@radix-ui/react-icons";
import { useNoticeApi } from "@/v2/entities/notice/Notice.api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";

function calculateLiveStatus(notice: Notice): number {
  const uniqueSimulators = new Set<string>();

  notice.noticeOutputs.forEach((output) => {
    if (output.enabled && output.simulatorId) {
      uniqueSimulators.add(output.simulatorId);
    }
  });

  return uniqueSimulators.size;
}

interface NoticeActionMenuProps {
  notice: Notice;
  liveCount: number;
  onDelete: () => Promise<void>;
  onDisableAll: () => Promise<void>;
}

function NoticeActionMenu({
  notice,
  liveCount,
  onDelete,
  onDisableAll,
}: NoticeActionMenuProps) {
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);

  const handleDisableConfirm = useCallback(async () => {
    setIsDisabling(true);
    try {
      await onDisableAll();
      setShowDisableDialog(false);
    } finally {
      setIsDisabling(false);
    }
  }, [onDisableAll]);

  const handleDeleteConfirm = useCallback(async () => {
    setIsDeleting(true);
    try {
      await onDelete();
      setShowDeleteDialog(false);
    } finally {
      setIsDeleting(false);
    }
  }, [onDelete]);

  const hasLiveOutputs = liveCount > 0;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton
            size="1"
            variant="ghost"
            color="gray"
            onClick={(e) => e.stopPropagation()}
          >
            <DotsVerticalIcon />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem
            disabled={!hasLiveOutputs}
            onClick={(e) => {
              e.stopPropagation();
              setShowDisableDialog(true);
            }}
          >
            Disable All Outputs
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={hasLiveOutputs}
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteDialog(true);
            }}
            className="text-red-600 focus:text-red-600"
          >
            Delete Notice
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog.Root open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <Dialog.Content maxWidth="450px">
          <Dialog.Title>Disable all outputs?</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            This will unpublish the notice from {liveCount} simulator
            {liveCount !== 1 ? "s" : ""}.
          </Dialog.Description>

          <Flex gap="3" mt="4" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setShowDisableDialog(false)}
              disabled={isDisabling}
            >
              Cancel
            </Button>
            <Button
              color="orange"
              onClick={handleDisableConfirm}
              disabled={isDisabling}
            >
              {isDisabling ? "Disabling..." : "Disable All"}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <Dialog.Root open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <Dialog.Content maxWidth="450px">
          <Dialog.Title>Delete notice?</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            This action cannot be undone.
          </Dialog.Description>

          <Flex gap="3" mt="4" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}

interface NoticeListItemProps extends ItemRendererProps<Notice> {
  onDelete: (noticeId: string) => Promise<void>;
  onDisableAll: (noticeId: string) => Promise<void>;
}

function NoticeListItem({ item, onDelete, onDisableAll }: NoticeListItemProps) {
  const liveCount = calculateLiveStatus(item);

  return (
    <Flex p="3" direction="column" gap="1">
      <Flex
        align="center"
        gap="2"
        justify="between"
        style={{ width: "100%" }}
      >
        <Flex align="center" gap="2">
          <Text size="2" weight="medium" highContrast>
            {item.name}
          </Text>

          <Badge color="gray" variant="soft" size="1">
            {item.version}
          </Badge>
        </Flex>

        <Flex align="center" gap="2">
          {liveCount > 0 ? (
            <Badge color="green" variant="soft" size="1">
              Live on {liveCount} simulator{liveCount !== 1 ? "s" : ""}
            </Badge>
          ) : (
            <Badge color="gray" variant="soft" size="1">
              Unpublished
            </Badge>
          )}

          <NoticeActionMenu
            notice={item}
            liveCount={liveCount}
            onDelete={() => onDelete(item.id)}
            onDisableAll={() => onDisableAll(item.id)}
          />
        </Flex>
      </Flex>
    </Flex>
  );
}

function noticeFilterFn(item: Notice, searchPhrase: string) {
  return item.name.toLowerCase().includes(searchPhrase.toLowerCase());
}

function noticeGetItemId(item: Notice) {
  return item.id;
}

interface NoticeListViewProps {
  notices?: Notice[];
  selectedNoticeId?: string;
  onSelect: (noticeId: string) => void;
  onRefetch: () => Promise<void>;
}

export function NoticeListView({
  notices = [],
  selectedNoticeId,
  onSelect,
  onRefetch,
}: NoticeListViewProps) {
  const { disableAllOutputs, deleteNotice } = useNoticeApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleDelete = useCallback(
    async (noticeId: string) => {
      try {
        await deleteNotice({ id: noticeId });

        // Always close the extended view and return to list
        navigate("/notices");

        toast.success("Notice deleted");

        // Invalidate all notices queries to refresh the list and detail views
        await queryClient.invalidateQueries(["notices"]);
        await onRefetch();
      } catch (error) {
        toast.error("Failed to delete notice. Please try again.");
      }
    },
    [deleteNotice, onRefetch, navigate, queryClient]
  );

  const handleDisableAll = useCallback(
    async (noticeId: string) => {
      try {
        await disableAllOutputs(noticeId);
        toast.success("All outputs disabled");

        // Invalidate all notices queries to refresh the list and detail views
        await queryClient.invalidateQueries(["notices"]);
        await onRefetch();
      } catch (error) {
        toast.error("Failed to disable outputs. Please try again.");
      }
    },
    [disableAllOutputs, onRefetch, queryClient]
  );

  const noticeRenderer = useCallback(
    (props: ItemRendererProps<Notice>) => (
      <NoticeListItem
        {...props}
        onDelete={handleDelete}
        onDisableAll={handleDisableAll}
      />
    ),
    [handleDelete, handleDisableAll]
  );

  return (
    <ListView
      items={notices}
      title="Notices"
      selectedItemId={selectedNoticeId}
      onSelect={onSelect}
      filterFn={noticeFilterFn}
      getItemId={noticeGetItemId}
      itemRenderer={noticeRenderer}
    />
  );
}
